import { apiClient } from "@finitless/shared/lib";

const META_ADS_API_URL = (import.meta.env.VITE_META_ADS_API_URL || "").trim();

/**
 * Base URL for the meta-ads Python service (Phase D). Production should sit behind the Finitless API proxy when available.
 */
export const getMetaAdsApiBaseUrl = () => META_ADS_API_URL.replace(/\/$/, "");

export const isMetaAdsApiConfigured = () => Boolean(getMetaAdsApiBaseUrl());

/** Full URL for GET /api/dashboard/oauth/start (no query — connection is global, not per store). */
export const getMetaAdsOAuthStartUrl = () =>
  (import.meta.env.VITE_META_ADS_OAUTH_START_URL || "").trim();

function authHeaders() {
  const headers = { "Content-Type": "application/json" };
  const token = apiClient.getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/**
 * GET /api/dashboard/campaigns/{campaignId} — Campaign + first ad set / creative for the editor.
 */
export async function fetchMetaAdsCampaignDetail(campaignId) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(campaignId || "").trim();
  if (!id) {
    throw new Error("Missing campaignId");
  }
  const enc = encodeURIComponent(id);
  const response = await fetch(`${base}/api/dashboard/campaigns/${enc}`, {
    method: "GET",
    headers: authHeaders(),
  });
  return parseJsonResponse(response);
}

/**
 * GET /api/dashboard/adsets/{adSetId} — Ad set-level details for the editor.
 */
export async function fetchMetaAdsAdSetDetail(adSetId) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(adSetId || "").trim();
  if (!id) {
    throw new Error("Missing adSetId");
  }
  const enc = encodeURIComponent(id);
  const response = await fetch(`${base}/api/dashboard/adsets/${enc}`, {
    method: "GET",
    headers: authHeaders(),
  });
  return parseJsonResponse(response);
}

/**
 * GET /api/dashboard/ads/{adId} — Ad-level details and creative fields for the editor.
 */
export async function fetchMetaAdsAdDetail(adId) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(adId || "").trim();
  if (!id) {
    throw new Error("Missing adId");
  }
  const enc = encodeURIComponent(id);
  const response = await fetch(`${base}/api/dashboard/ads/${enc}`, {
    method: "GET",
    headers: authHeaders(),
  });
  return parseJsonResponse(response);
}

/**
 * GET /api/dashboard/meta/connection — whether a Meta token is stored (global credentials row).
 */
export async function fetchMetaAdsConnectionStatus() {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const response = await fetch(`${base}/api/dashboard/meta/connection`, {
    method: "GET",
    headers: authHeaders(),
  });
  const data = await parseJsonResponse(response);
  return {
    connected: Boolean(data.connected),
    adAccountId: data.adAccountId != null ? String(data.adAccountId) : null,
  };
}

/**
 * DELETE /api/dashboard/meta/connection — clear stored Meta token and global ad account row.
 */
export async function disconnectMetaAdsConnection() {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const response = await fetch(`${base}/api/dashboard/meta/connection`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return parseJsonResponse(response);
}

/**
 * GET /api/dashboard/campaigns|adsets|ads/{id}/insights/daily — daily time series (time_increment=1).
 * @param {"campaign"|"adset"|"ad"} entityType
 * @param {string} entityId
 * @param {string} [datePreset="last_30d"]
 */
export async function fetchMetaAdsInsightsDaily(
  entityType,
  entityId,
  datePreset = "last_30d",
) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(entityId || "").trim();
  if (!id) {
    throw new Error("Missing entity id");
  }
  const type = String(entityType || "").toLowerCase();
  const segment =
    type === "campaign"
      ? `campaigns/${encodeURIComponent(id)}/insights/daily`
      : type === "adset"
        ? `adsets/${encodeURIComponent(id)}/insights/daily`
        : type === "ad"
          ? `ads/${encodeURIComponent(id)}/insights/daily`
          : null;
  if (!segment) {
    throw new Error("Invalid entity type for insights");
  }
  const preset = String(datePreset || "last_30d").trim() || "last_30d";
  const q = new URLSearchParams({ date_preset: preset });
  const response = await fetch(
    `${base}/api/dashboard/${segment}?${q.toString()}`,
    {
      method: "GET",
      headers: authHeaders(),
    },
  );
  const data = await parseJsonResponse(response);
  return {
    objectId: data.objectId ?? id,
    objectKind: data.objectKind ?? type,
    datePreset: data.datePreset ?? preset,
    series: Array.isArray(data.series) ? data.series : [],
  };
}

/**
 * GET /api/dashboard/ads/{adId}/preview — Meta Graph iframe/HTML body (mobile feed standard).
 */
export async function fetchMetaAdsAdPreview(
  adId,
  adFormat = "MOBILE_FEED_STANDARD",
) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(adId || "").trim();
  if (!id) {
    throw new Error("Missing adId");
  }
  const enc = encodeURIComponent(id);
  const fmt = String(adFormat || "MOBILE_FEED_STANDARD").trim();
  const q = new URLSearchParams({ ad_format: fmt });
  const response = await fetch(
    `${base}/api/dashboard/ads/${enc}/preview?${q.toString()}`,
    {
      method: "GET",
      headers: authHeaders(),
    },
  );
  return parseJsonResponse(response);
}

/**
 * POST /api/dashboard/ads/{adId}/preview-live — ``generatepreviews`` iframe/HTML body (mobile feed).
 * @param {string} adId
 * @param {{ destinationUrl: string, imageUrl: string, primaryText: string, pageId: string, campaignObjective?: string }} payload
 */
export async function fetchMetaAdsAdPreviewLive(adId, payload) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(adId || "").trim();
  if (!id) {
    throw new Error("Missing adId");
  }
  const enc = encodeURIComponent(id);
  const body = {
    destinationUrl: String(payload?.destinationUrl ?? "").trim(),
    imageUrl: String(payload?.imageUrl ?? "").trim(),
    primaryText: String(payload?.primaryText ?? "").trim(),
    pageId: String(payload?.pageId ?? "").trim(),
  };
  const co = payload?.campaignObjective;
  if (co != null && String(co).trim()) {
    body.campaignObjective = String(co).trim();
  }
  const adFormat = String(payload?.adFormat ?? "MOBILE_FEED_STANDARD").trim();
  if (adFormat) {
    body.adFormat = adFormat.toUpperCase();
  }
  const response = await fetch(
    `${base}/api/dashboard/ads/${enc}/preview-live`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    },
  );
  return parseJsonResponse(response);
}

async function parseJsonResponse(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      data.detail ||
      data.message ||
      data.error ||
      `Meta Ads request failed (${response.status})`;
    throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
  }
  return data;
}

/**
 * GET /api/dashboard/ad-account/campaigns — live campaigns from Meta Graph + insights.
 * @param {string} [datePreset="last_30d"] — Meta Marketing API `date_preset` (e.g. last_7d, last_30d).
 */
export async function fetchMetaAdsAdAccountCampaigns(datePreset = "last_30d") {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const preset = String(datePreset || "last_30d").trim() || "last_30d";
  const q = new URLSearchParams({ date_preset: preset });
  const response = await fetch(
    `${base}/api/dashboard/ad-account/campaigns?${q.toString()}`,
    {
      method: "GET",
      headers: authHeaders(),
    },
  );
  const data = await parseJsonResponse(response);
  return {
    source: data.source ?? "graph",
    adAccountId: data.adAccountId ?? null,
    datePreset: data.datePreset ?? null,
    storeId: data.storeId ?? null,
    totalSpendCents: data.totalSpendCents,
    campaigns: (data.campaigns ?? []).map((c) => ({
      ...c,
      campaignId: c.campaignId,
      internalName: c.internalName,
      status: c.status,
      objective: c.objective,
      spendCentsSum: c.spendCentsSum,
      impressionsSum: c.impressionsSum,
      clicksSum: c.clicksSum,
    })),
    adSets: (data.adSets ?? []).map((adSet) => ({
      ...adSet,
      adSetId: adSet.adSetId,
      internalName: adSet.internalName,
      status: adSet.status,
      campaignId: adSet.campaignId,
      campaignName: adSet.campaignName,
      optimizationGoal: adSet.optimizationGoal,
      dailyBudgetCents: adSet.dailyBudgetCents,
      spendCentsSum: adSet.spendCentsSum,
      impressionsSum: adSet.impressionsSum,
      clicksSum: adSet.clicksSum,
    })),
    ads: (data.ads ?? []).map((ad) => ({
      ...ad,
      adId: ad.adId,
      internalName: ad.internalName,
      status: ad.status,
      campaignId: ad.campaignId,
      campaignName: ad.campaignName,
      adSetId: ad.adSetId,
      adSetName: ad.adSetName,
      creativeId: ad.creativeId,
      spendCentsSum: ad.spendCentsSum,
      impressionsSum: ad.impressionsSum,
      clicksSum: ad.clicksSum,
    })),
    ordersAttributed:
      data.ordersAttributed != null ? data.ordersAttributed : null,
    revenueCents:
      data.revenueCents != null
        ? data.revenueCents
        : data.revenueAttributed != null
          ? data.revenueAttributed
          : null,
    roas: data.roas != null ? data.roas : null,
  };
}

/**
 * POST /api/dashboard/oauth/exchange-long-lived
 */
export async function exchangeMetaLongLivedToken(body) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const response = await fetch(
    `${base}/api/dashboard/oauth/exchange-long-lived`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    },
  );
  return parseJsonResponse(response);
}

/**
 * POST /api/dashboard/campaigns/launch — access token loaded server-side when omitted.
 */
export async function launchMetaCampaign(body) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const response = await fetch(`${base}/api/dashboard/campaigns/launch`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return parseJsonResponse(response);
}

/**
 * PATCH /api/dashboard/campaigns/{campaignId}/status — pause or activate (Meta `status`).
 * @param {string} campaignId - Graph campaign id (numeric string)
 * @param {"ACTIVE"|"PAUSED"} status
 */
export async function patchMetaAdsCampaignStatus(campaignId, status) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(campaignId || "").trim();
  if (!id) {
    throw new Error("Missing campaignId");
  }
  const s = String(status || "").toUpperCase();
  if (s !== "ACTIVE" && s !== "PAUSED") {
    throw new Error("status must be ACTIVE or PAUSED");
  }
  const enc = encodeURIComponent(id);
  const response = await fetch(
    `${base}/api/dashboard/campaigns/${enc}/status`,
    {
      method: "PATCH",
      headers: authHeaders(),
      body: JSON.stringify({ status: s }),
    },
  );
  return parseJsonResponse(response);
}

/**
 * PATCH /api/dashboard/campaigns/{campaignId} — save edits (campaign + ad set + creative when needed).
 * @param {string} campaignId - Graph campaign id
 * @param {object} body - CamelCase aligned with launch
 */
export async function patchMetaAdsCampaignBundle(campaignId, body) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(campaignId || "").trim();
  if (!id) {
    throw new Error("Missing campaignId");
  }
  const enc = encodeURIComponent(id);
  const response = await fetch(`${base}/api/dashboard/campaigns/${enc}`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return parseJsonResponse(response);
}

/**
 * PATCH /api/dashboard/campaigns/{campaignId}/settings — campaign-level settings only.
 * Does not update ad sets, ads, creatives, destination URLs, or budgets.
 */
export async function patchMetaAdsCampaignSettings(campaignId, body) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(campaignId || "").trim();
  if (!id) {
    throw new Error("Missing campaignId");
  }
  const enc = encodeURIComponent(id);
  const response = await fetch(
    `${base}/api/dashboard/campaigns/${enc}/settings`,
    {
      method: "PATCH",
      headers: authHeaders(),
      body: JSON.stringify(body),
    },
  );
  return parseJsonResponse(response);
}

/**
 * PATCH /api/dashboard/adsets/{adSetId} — ad set-level settings only.
 */
export async function patchMetaAdsAdSet(adSetId, body) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(adSetId || "").trim();
  if (!id) {
    throw new Error("Missing adSetId");
  }
  const enc = encodeURIComponent(id);
  const response = await fetch(`${base}/api/dashboard/adsets/${enc}`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return parseJsonResponse(response);
}

/**
 * PATCH /api/dashboard/ads/{adId} — ad-level settings and creative fields.
 */
export async function patchMetaAdsAd(adId, body) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(adId || "").trim();
  if (!id) {
    throw new Error("Missing adId");
  }
  const enc = encodeURIComponent(id);
  const response = await fetch(`${base}/api/dashboard/ads/${enc}`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return parseJsonResponse(response);
}

/**
 * DELETE /api/dashboard/adsets/{adSetId} — removes an ad set in Meta.
 */
export async function deleteMetaAdsAdSet(adSetId) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(adSetId || "").trim();
  if (!id) {
    throw new Error("Missing adSetId");
  }
  const enc = encodeURIComponent(id);
  const response = await fetch(`${base}/api/dashboard/adsets/${enc}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return parseJsonResponse(response);
}

/**
 * DELETE /api/dashboard/campaigns/{campaignId} — removes campaign in Meta and DB mirror.
 */
export async function deleteMetaAdsCampaign(campaignId) {
  const base = getMetaAdsApiBaseUrl();
  if (!base) {
    throw new Error("META_ADS_API_NOT_CONFIGURED");
  }
  const id = String(campaignId || "").trim();
  if (!id) {
    throw new Error("Missing campaignId");
  }
  const enc = encodeURIComponent(id);
  const response = await fetch(`${base}/api/dashboard/campaigns/${enc}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return parseJsonResponse(response);
}
